import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import { ButtonComponent, SignalButton, SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';
import { importLibrary } from '@googlemaps/js-api-loader';
import { MapLocationValue } from '@shared/components/map-location-picker/map-location-picker';
import { configureGoogleMaps } from '@core/config/google-maps';
import {
  GoogleAddressAutoComplete
} from '@shared/components/google-address-auto-complete/google-address-auto-complete';

interface MapConfigModel {
  label: string;
  helpText: string;
  required: boolean;
}

@Component({
  selector: 'app-map-config-view',
  standalone: true,
  templateUrl: './map-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SignalCheckbox,
    SignalFormDirective,
    SignalTextField,
    SignalFormField,
    GoogleAddressAutoComplete,
    SignalButton,
  ],
})
export class MapConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<MapConfigModel>({
    label: '',
    helpText: '',
    required: false,
  });

  readonly defaultLocation = signal<MapLocationValue | null>(null);

  readonly configForm = form(this.model);
  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        required: next.required ?? validation.required ?? false,
      });
      this.defaultLocation.set(next.mapDefaultLocation ?? null);
    });

    effect(() => {
      const draft = this.model();
      const defaultLocation = this.defaultLocation();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        mapDefaultLocation: defaultLocation,
        validation: {
          required: draft.required,
        },
      });
    });
  }

  onLocationSelected(place: google.maps.places.Place): void {
    const location = place.location;
    if (!location) {
      return;
    }
    this.defaultLocation.set({
      address: place.shortFormattedAddress ?? place.displayName ?? '',
      latitude: location.lat(),
      longitude: location.lng(),
      placeId: place.id ?? null,
    });
  }

  async useCurrentLocation(): Promise<void> {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        const location = await this.getAddressFromCoordinates(latitude, longitude);

        this.defaultLocation.set(location);
      },
      (error) => {
        console.error('Unable to get current location:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }
  private async getAddressFromCoordinates(
    latitude: number,
    longitude: number,
  ): Promise<MapLocationValue> {
    configureGoogleMaps();

    const { Geocoder } = (await importLibrary('geocoding')) as google.maps.GeocodingLibrary;

    const geocoder = new Geocoder();

    const response = await geocoder.geocode({
      location: {
        lat: latitude,
        lng: longitude,
      },
    });

    const result = response.results[0];

    const address = result?.formatted_address ?? 'Current location';

    return {
      address: address,
      latitude,
      longitude,
      placeId: result?.place_id ?? null,
    };
  }
}
